import {
  Box,
  Button,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { useModalControl } from 'hooks/useModalControl';

import BorderStack from 'components/@common/BorderStack';
import Icon from 'components/@icons/Icon';
import { SelectedSeat } from 'components/@seating/types';

import Seat from './Seat';
import SeatsDialog from './SeatsDialog';

interface Props {
  selectedSeats: Array<SelectedSeat>;
  subtitle: string | null;
  onRemoveSeat: (seat: SelectedSeat) => void;
  onSubmit: () => void;
}

const SelectedSeats = ({
  selectedSeats,
  subtitle,
  onRemoveSeat,
  onSubmit,
}: Props) => {
  const { open, onOpen, onClose } = useModalControl();
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const handleRemoveSeat = async (seat: SelectedSeat) => {
    onRemoveSeat(seat);
    await seat.deselect();
  };

  // When we remove the last item, close the drawer
  useEffect(() => {
    if (isMobile && !selectedSeats.length) {
      onClose();
    }
  }, [isMobile, selectedSeats.length, onClose]);

  return (
    <>
      <BorderStack
        sx={(theme) => ({
          py: 3,
          pt: { xs: 2, md: 3 },
          gridArea: 'selection',
          border: { xs: 'none', md: `1px solid ${theme.vars.palette.divider}` },
        })}
      >
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Box
            onClick={isMobile ? onOpen : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="extraSmall">
              {!selectedSeats.length ? (
                <FormattedMessage id="event.seating.selecting_for_date" />
              ) : (
                <FormattedMessage
                  id="event.seating.selected_for_date"
                  values={{ amount: selectedSeats.length }}
                />
              )}{' '}
              <Typography
                variant="extraSmall"
                component="span"
                sx={{
                  color: 'global01.400',
                  fontWeight: 700,
                }}
              >
                {subtitle}
              </Typography>
            </Typography>
            {isMobile && (
              <Icon
                name="ChevronDown"
                sx={{
                  visibility: !!selectedSeats.length ? 'visible' : 'hidden',
                }}
              />
            )}
          </Box>

          {!!selectedSeats.length && !isMobile && (
            <Stack
              sx={{
                gap: 1,
                display: { xs: 'none', md: 'flex' },
              }}
            >
              {selectedSeats.map((seat) => (
                <Seat
                  key={seat.uuid}
                  seat={seat}
                  onRemoveSeat={handleRemoveSeat}
                />
              ))}
            </Stack>
          )}
          <Button
            variant="outlined"
            color="secondary"
            disabled={!selectedSeats.length}
            onClick={onSubmit}
          >
            <FormattedMessage id="event.seating.continue" />
          </Button>
        </Stack>
      </BorderStack>
      {/* Only render the drawer on mobile viewport */}
      {isMobile && (
        <SeatsDialog subtitle={subtitle} open={open} onClose={onClose}>
          <Stack
            sx={{
              gap: 1,
            }}
          >
            {selectedSeats.map((seat) => (
              <Seat
                key={seat.uuid}
                seat={seat}
                onRemoveSeat={handleRemoveSeat}
              />
            ))}
          </Stack>
        </SeatsDialog>
      )}
    </>
  );
};

export default SelectedSeats;
